import { EFinancingCompany, EFinancingProgram, ELoanPurpose } from '@if/core/types'
import {
  ActionButton,
  ActionMenuItem,
  Banner,
  ControlledAutocomplete,
  ControlledCardCheckbox,
  ControlledNumberField,
  ControlledPhoneField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  type FormDialogContentContext,
  TranslatedError,
  useCan,
  useFormContainerContext,
  useFormDialog,
  useIfUser,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Link } from '@mui/material'
import { access } from '@src/access'
import { useSendInvitation } from '@src/data/api/invitation-api/invitation-api'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { companyConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { getLanguageOrder, toELanguage } from '@src/data/language-selectors'
import { useProfileLanguage } from '@src/hooks/language-hooks'
import { FinancingProgramConfigs, isMedicalServiceCategory } from '@src/types/Constants'
import { InvitationHistoryEntryDto } from '@src/types/InvitationHistoryDto'
import { ECommunicationType, InvitationFormData, invitationSchema } from '@src/types/InvitationSchema'
import { History, Send, UserPlus } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useInvitationHistoryDialog } from './InvitationHistoryDialog'

/** Open-time context for the invite dialog — the merchant's company + loan purpose. */
export interface InvitationOpenProps {
  financingCompanyId?: EFinancingCompany
  loanPurposeId?: ELoanPurpose | null
}

type InvitationCtx = FormDialogContentContext<InvitationFormData, InvitationOpenProps, void>

/** Shared by the throw and the presentation override, so the two cannot drift apart. */
const DUPLICATE_BLOCKED_KEY = 'invitationDialog.duplicateBlocked'

const shouldShowField = (communicationType: ECommunicationType, fieldType: ECommunicationType): boolean => {
  if (fieldType === ECommunicationType.Email) {
    return communicationType === ECommunicationType.Email || communicationType === ECommunicationType.Both
  }
  if (fieldType === ECommunicationType.SMS) {
    return communicationType === ECommunicationType.SMS || communicationType === ECommunicationType.Both
  }
  return false
}

const isEmailChannel = (type: ECommunicationType) =>
  type === ECommunicationType.Email || type === ECommunicationType.Both
const isSmsChannel = (type: ECommunicationType) => type === ECommunicationType.SMS || type === ECommunicationType.Both

/**
 * The History entry point. It lives in the footer's far-left slot rather than the body: it is a
 * secondary way *into* the dialog's data, not a field, and above the form it pushed every input
 * down a row and read as a label for First name.
 */
const InvitationHistoryAction = ({ ctx }: { ctx: InvitationCtx }) => {
  const { t } = useTranslation()
  const { setValue } = ctx.form
  const historyDialog = useInvitationHistoryDialog()

  // The History endpoint answers 404 while the module is off, so the entry point has to follow the
  // same switch rather than opening onto a load error.
  const showHistory = useCan(access.invitationHistory)

  // Resend refills the form from the history entry rather than sending straight away, so the user sees
  // exactly what goes out and can change the language, which is the one way past the duplicate guard.
  const refillFrom = (entry: InvitationHistoryEntryDto, merchantId: string | null) => {
    const recipientFor = (type: ECommunicationType): string =>
      entry.channels.find((channel) => channel.communicationType === type)?.recipient ?? ''

    setValue('firstName', entry.firstName ?? '', { shouldValidate: true })
    setValue('lastName', entry.lastName ?? '', { shouldValidate: true })
    setValue('languageId', entry.languageId, { shouldValidate: true })
    setValue('communicationType', entry.communicationType ?? ECommunicationType.Both, { shouldValidate: true })
    setValue('email', recipientFor(ECommunicationType.Email), { shouldValidate: true })
    setValue('mobilePhoneNumber', recipientFor(ECommunicationType.SMS), { shouldValidate: true })
    setValue('amount', entry.amount ?? null, { shouldValidate: true })
    setValue('resentFromInvitationId', entry.id)
    // Resend under the same subsidiary the history was read for, not the previously selected one.
    setValue('merchantId', merchantId, { shouldValidate: true })
  }

  const handleHistory = async () => {
    const result = await historyDialog.open()

    if (result) {
      refillFrom(result.entry, result.merchantId)
    }
  }

  if (!showHistory) {
    return null
  }

  return (
    <ActionButton startIcon={History} onClick={handleHistory}>
      {t('invitationDialog.history')}
    </ActionButton>
  )
}

const InvitationDialogContent = ({ ctx }: { ctx: InvitationCtx }) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()
  const { control } = ctx.form
  const isIceberg = ctx.props.financingCompanyId === EFinancingCompany.Iceberg
  const profileLanguage = useProfileLanguage()
  const user = useIfUser()

  const languageList = useTranslatedOptionEnum('ELanguage', getLanguageOrder(profileLanguage), 'enum')
  const financingProgramList = useTranslatedOptionEnum(
    'EFinancingProgram',
    [EFinancingProgram.MechanicalWarranty],
    'enum',
  )

  const communicationType = useWatch({ control, name: 'communicationType' })
  const financingProgram = useWatch({ control, name: 'financingProgramId' })
  const selectedMerchantId = useWatch({ control, name: 'merchantId' })
  const config = companyConfigByFinancingProgramId(financingProgram)
  const link = resolvedLanguage === 'fr' ? config.privacyPolicyLinkFR : config.privacyPolicyLinkEN

  // Banner users pick which subsidiary they invite on behalf of; standard users have none.
  const [subsidiaries] = useMerchantByParentId(user.isBannerUser ? user.merchantId : '')
  const filteredSubsidiaries = useMemo(
    () => (subsidiaries ?? []).filter((m) => m.isActive && m.supportedFinancingPrograms.includes(financingProgram)),
    [subsidiaries, financingProgram],
  )

  // The amount ceiling follows the chosen merchant's service category: a banner user picks a
  // subsidiary (falling back to the open props category until one is selected), a standard user
  // carries their own (from open props). Medicard categories (Medical, Dentistry, Aesthetics) get a
  // higher ceiling. Fed to the schema as $maxLoanValue.
  const { setValidationContext } = useFormContainerContext<InvitationFormData>()
  const subsidiary = filteredSubsidiaries.find((m) => m.id === selectedMerchantId)
  const effectiveServiceCategory =
    user.isBannerUser && subsidiary ? subsidiary.serviceCategory : ctx.props.loanPurposeId
  const personalConfig = FinancingProgramConfigs[EFinancingProgram.Personal]
  const maxLoanAmount = isMedicalServiceCategory(effectiveServiceCategory)
    ? personalConfig.maxLoanAmountMedicard
    : personalConfig.maxLoanAmount

  useEffect(() => {
    setValidationContext('maxLoanValue', maxLoanAmount)
  }, [maxLoanAmount, setValidationContext])

  return (
    <FieldGrid columns={{ xs: 1, sm: 2 }}>
      {user.isBannerUser && (
        <ControlledAutocomplete
          control={control}
          name="merchantId"
          label={t('invitationDialog.merchant')}
          span="full"
          options={filteredSubsidiaries.map((m) => m.id)}
          getOptionKey={(id) => id ?? ''}
          getOptionLabel={(id) => filteredSubsidiaries.find((m) => m.id === id)?.name ?? ''}
          disableClearable
        />
      )}
      {isIceberg && (
        <ControlledSelect
          control={control}
          name="financingProgramId"
          label={t('invitationDialog.financingProgram')}
          disabled
          options={financingProgramList}
          disableEmptyOption
          span="full"
        />
      )}
      <ControlledTextField control={control} name="firstName" label={t('common.firstName')} />
      <ControlledTextField control={control} name="lastName" label={t('common.lastName')} />
      <ControlledSelect
        control={control}
        name="languageId"
        label={t('invitationDialog.preferredLanguage')}
        options={languageList}
        disableEmptyOption
        span="full"
      />
      <ControlledSelect
        control={control}
        name="communicationType"
        label={t('invitationDialog.communicationType')}
        disableEmptyOption
        span="full"
        options={[
          <ActionMenuItem key={ECommunicationType.Email} value={ECommunicationType.Email}>
            {t('invitationDialog.email')}
          </ActionMenuItem>,
          <ActionMenuItem key={ECommunicationType.SMS} value={ECommunicationType.SMS}>
            {t('invitationDialog.sms')}
          </ActionMenuItem>,
          <ActionMenuItem key={ECommunicationType.Both} value={ECommunicationType.Both}>
            {t('invitationDialog.emailAndSms')}
          </ActionMenuItem>,
        ]}
      />
      {shouldShowField(communicationType, ECommunicationType.Email) && (
        <ControlledTextField
          control={control}
          name="email"
          label={t('invitationDialog.email')}
          type="email"
          span="full"
        />
      )}
      {shouldShowField(communicationType, ECommunicationType.SMS) && (
        <ControlledPhoneField control={control} name="mobilePhoneNumber" label={t('common.phone')} span="full" />
      )}
      {financingProgram === EFinancingProgram.Personal && (
        <ControlledNumberField
          control={control}
          name="amount"
          label={t('invitationDialog.amount')}
          isMoney
          disableDecimals
          span="full"
        />
      )}
      <ControlledCardCheckbox
        control={control}
        name="confirmationChecked"
        span="full"
        label={<Trans i18nKey="invitationDialog.consent" components={{ a: <Link href={link} target="_blank" /> }} />}
      />
    </FieldGrid>
  )
}

/**
 * The Invite Applicant dialog, wired through the shared `useFormDialog` API: the
 * container owns the form, submit/loading state, the success snackbar and error
 * surface. `open(props)` shows it seeded from the merchant's company/loan purpose;
 * a partial email/SMS failure throws so the dialog stays open with the error.
 *
 * Safe to call from several triggers (the sidebar action + the home button): the
 * hook only registers a handle — `useFormDialog` is mount-on-open, so the dialog
 * DOM is portaled in only while open, never one node per caller at rest.
 */
export const useInvitationDialog = () => {
  const { t } = useTranslation()
  const profileLanguage = useProfileLanguage()
  const user = useIfUser()
  const [sendInvitation] = useSendInvitation()

  const dialog = useFormDialog<InvitationFormData, InvitationOpenProps, void>({
    icon: UserPlus,
    title: t('invitationDialog.title'),
    subtitle: t('invitationDialog.subtitle'),
    confirmLabel: t('invitationDialog.submit'),
    confirmIcon: Send,
    maxWidth: 'sm',
    fullWidth: true,
    schema: invitationSchema,
    defaultValues: (props) => ({
      firstName: '',
      lastName: '',
      languageId: toELanguage(profileLanguage),
      communicationType: ECommunicationType.Both,
      email: '',
      mobilePhoneNumber: '',
      confirmationChecked: false,
      resentFromInvitationId: null,
      isBannerUser: user.isBannerUser,
      merchantId: null,
      amount: null,
      financingProgramId:
        props.financingCompanyId === EFinancingCompany.Iceberg
          ? EFinancingProgram.MechanicalWarranty
          : EFinancingProgram.Personal,
    }),
    content: (ctx) => <InvitationDialogContent ctx={ctx} />,
    additionalActions: (ctx) => <InvitationHistoryAction ctx={ctx} />,
    onError: {
      // The duplicate guard is a business rule, not a failure: it keeps the dialog open on the same
      // band, in a warning tone. Matched on the error's key rather than its text, so it holds in both
      // languages; a real send failure returns undefined and falls through to the app registry.
      translated: (descriptor) =>
        descriptor.raw instanceof TranslatedError && descriptor.raw.key === DUPLICATE_BLOCKED_KEY
          ? { role: 'contextual', node: <Banner severity="warning">{t(DUPLICATE_BLOCKED_KEY)}</Banner> }
          : undefined,
    },
    successMessage: t('invitationDialog.sendSuccess'),
    onSubmit: async (data, ctx) => {
      const result = await sendInvitation({
        firstName: data.firstName,
        lastName: data.lastName,
        languageId: data.languageId,
        communicationType: data.communicationType,
        email: shouldShowField(data.communicationType, ECommunicationType.Email) ? data.email || null : null,
        mobilePhoneNumber: shouldShowField(data.communicationType, ECommunicationType.SMS)
          ? data.mobilePhoneNumber || null
          : null,
        financingProgramId: data.financingProgramId,
        loanPurposeId: ctx.props.loanPurposeId ?? null,
        resentFromInvitationId: data.resentFromInvitationId ?? null,
        merchantId: data.isBannerUser ? data.merchantId || null : null,
        // Personal only; other programs never render the field, but gate it here too.
        amount: data.financingProgramId === EFinancingProgram.Personal ? (data.amount ?? null) : null,
      })

      // The credit side refused the send as a same-day duplicate: nothing went out, so the dialog
      // stays open and says why instead of showing a channel failure.
      if (result.duplicateBlocked) {
        throw new TranslatedError(DUPLICATE_BLOCKED_KEY)
      }

      // The mutation succeeds (HTTP 200) but may report a per-channel failure —
      // throw so the dialog stays open and surfaces it (email takes precedence).
      if (result.emailFailed && isEmailChannel(data.communicationType)) {
        throw new TranslatedError('invitationDialog.emailSendFailed')
      }
      if (result.smsFailed && isSmsChannel(data.communicationType)) {
        throw new TranslatedError('invitationDialog.smsSendFailed')
      }
    },
  })

  return { open: (props: InvitationOpenProps) => dialog.create(props) }
}
